// Config-driven unit-conversion engine shared by every /tools/*-converter page.
// All math runs in the browser: each unit declares how it maps to a canonical
// base unit (linear factor for most, affine functions for temperature), so one
// component + one config table powers the whole converter cluster.

export type UnitDefinition = {
  id: string;
  name: string;
  symbol: string;
  toBase: (value: number) => number;
  fromBase: (value: number) => number;
};

export type UnitCategory = {
  key: UnitCategoryKey;
  label: string;
  measure: string;
  units: UnitDefinition[];
  defaultFrom: string;
  defaultTo: string;
  sampleValue: number;
};

export type UnitCategoryKey =
  | 'length'
  | 'weight'
  | 'temperature'
  | 'area'
  | 'volume'
  | 'speed'
  | 'data-size'
  | 'time'
  | 'pressure'
  | 'energy'
  | 'power'
  | 'angle'
  | 'frequency'
  | 'torque'
  | 'voltage'
  | 'current'
  | 'charge'
  | 'illuminance'
  | 'apparent-power'
  | 'reactive-power'
  | 'reactive-energy'
  | 'volumetric-flow-rate';

function linear(factor: number): Pick<UnitDefinition, 'toBase' | 'fromBase'> {
  return {
    toBase: (value) => value * factor,
    fromBase: (value) => value / factor,
  };
}

function u(id: string, name: string, symbol: string, factor: number): UnitDefinition {
  return { id, name, symbol, ...linear(factor) };
}

export const unitCategories: Record<UnitCategoryKey, UnitCategory> = {
  length: {
    key: 'length',
    label: 'Length',
    measure: 'length',
    defaultFrom: 'm',
    defaultTo: 'ft',
    sampleValue: 1,
    units: [
      u('um', 'Micrometer', 'µm', 0.000001),
      u('mm', 'Millimeter', 'mm', 0.001),
      u('cm', 'Centimeter', 'cm', 0.01),
      u('m', 'Meter', 'm', 1),
      u('km', 'Kilometer', 'km', 1000),
      u('in', 'Inch', 'in', 0.0254),
      u('ft', 'Foot', 'ft', 0.3048),
      u('yd', 'Yard', 'yd', 0.9144),
      u('mi', 'Mile', 'mi', 1609.344),
      u('nmi', 'Nautical mile', 'nmi', 1852),
    ],
  },
  weight: {
    key: 'weight',
    label: 'Weight',
    measure: 'mass',
    defaultFrom: 'kg',
    defaultTo: 'lb',
    sampleValue: 1,
    units: [
      u('mg', 'Milligram', 'mg', 0.000001),
      u('g', 'Gram', 'g', 0.001),
      u('kg', 'Kilogram', 'kg', 1),
      u('t', 'Metric ton', 't', 1000),
      u('ton', 'US ton (short ton)', 'ton', 907.18474),
      u('oz', 'Ounce', 'oz', 0.028349523125),
      u('lb', 'Pound', 'lb', 0.45359237),
      u('st', 'Stone', 'st', 6.35029318),
    ],
  },
  temperature: {
    key: 'temperature',
    label: 'Temperature',
    measure: 'temperature',
    defaultFrom: 'c',
    defaultTo: 'f',
    sampleValue: 20,
    units: [
      {
        id: 'c',
        name: 'Celsius',
        symbol: '°C',
        toBase: (v) => v,
        fromBase: (v) => v,
      },
      {
        id: 'f',
        name: 'Fahrenheit',
        symbol: '°F',
        toBase: (v) => (v - 32) / 1.8,
        fromBase: (v) => v * 1.8 + 32,
      },
      {
        id: 'k',
        name: 'Kelvin',
        symbol: 'K',
        toBase: (v) => v - 273.15,
        fromBase: (v) => v + 273.15,
      },
    ],
  },
  area: {
    key: 'area',
    label: 'Area',
    measure: 'area',
    defaultFrom: 'm2',
    defaultTo: 'ft2',
    sampleValue: 1,
    units: [
      u('mm2', 'Square millimeter', 'mm²', 0.000001),
      u('cm2', 'Square centimeter', 'cm²', 0.0001),
      u('m2', 'Square meter', 'm²', 1),
      u('ha', 'Hectare', 'ha', 10000),
      u('km2', 'Square kilometer', 'km²', 1000000),
      u('in2', 'Square inch', 'in²', 0.00064516),
      u('ft2', 'Square foot', 'ft²', 0.09290304),
      u('yd2', 'Square yard', 'yd²', 0.83612736),
      u('ac', 'Acre', 'ac', 4046.8564224),
      u('mi2', 'Square mile', 'mi²', 2589988.110336),
    ],
  },
  volume: {
    key: 'volume',
    label: 'Volume',
    measure: 'volume',
    defaultFrom: 'l',
    defaultTo: 'gal',
    sampleValue: 1,
    units: [
      u('ml', 'Milliliter', 'ml', 0.001),
      u('l', 'Liter', 'l', 1),
      u('m3', 'Cubic meter', 'm³', 1000),
      u('tsp', 'Teaspoon (US)', 'tsp', 0.00492892159375),
      u('tbsp', 'Tablespoon (US)', 'tbsp', 0.01478676478125),
      u('cup', 'Cup (US)', 'cup', 0.2365882365),
      u('pt', 'Pint (US)', 'pt', 0.473176473),
      u('qt', 'Quart (US)', 'qt', 0.946352946),
      u('gal', 'Gallon (US)', 'gal', 3.785411784),
      u('floz', 'Fluid ounce (US)', 'fl oz', 0.0295735295625),
    ],
  },
  speed: {
    key: 'speed',
    label: 'Speed',
    measure: 'speed',
    defaultFrom: 'kmh',
    defaultTo: 'mph',
    sampleValue: 100,
    units: [
      u('ms', 'Meter per second', 'm/s', 1),
      u('kmh', 'Kilometer per hour', 'km/h', 0.2777777777777778),
      u('mph', 'Mile per hour', 'mph', 0.44704),
      u('fts', 'Foot per second', 'ft/s', 0.3048),
      u('kn', 'Knot', 'kn', 0.5144444444444445),
    ],
  },
  'data-size': {
    key: 'data-size',
    label: 'Data size',
    measure: 'digital storage',
    defaultFrom: 'mb',
    defaultTo: 'mib',
    sampleValue: 1,
    units: [
      u('bit', 'Bit', 'bit', 0.125),
      u('byte', 'Byte', 'B', 1),
      u('kb', 'Kilobyte', 'KB', 1000),
      u('mb', 'Megabyte', 'MB', 1000000),
      u('gb', 'Gigabyte', 'GB', 1000000000),
      u('tb', 'Terabyte', 'TB', 1000000000000),
      u('kib', 'Kibibyte', 'KiB', 1024),
      u('mib', 'Mebibyte', 'MiB', 1048576),
      u('gib', 'Gibibyte', 'GiB', 1073741824),
      u('tib', 'Tebibyte', 'TiB', 1099511627776),
    ],
  },
  time: {
    key: 'time',
    label: 'Time',
    measure: 'duration',
    defaultFrom: 'min',
    defaultTo: 's',
    sampleValue: 1,
    units: [
      u('ms', 'Millisecond', 'ms', 0.001),
      u('s', 'Second', 's', 1),
      u('min', 'Minute', 'min', 60),
      u('h', 'Hour', 'h', 3600),
      u('d', 'Day', 'd', 86400),
      u('wk', 'Week', 'wk', 604800),
      u('mo', 'Month (30d)', 'mo', 2592000),
      u('yr', 'Year (365d)', 'yr', 31536000),
    ],
  },
  pressure: {
    key: 'pressure',
    label: 'Pressure',
    measure: 'pressure',
    defaultFrom: 'bar',
    defaultTo: 'psi',
    sampleValue: 1,
    units: [
      u('pa', 'Pascal', 'Pa', 1),
      u('kpa', 'Kilopascal', 'kPa', 1000),
      u('bar', 'Bar', 'bar', 100000),
      u('psi', 'Pound per square inch', 'psi', 6894.757293168361),
      u('atm', 'Atmosphere', 'atm', 101325),
      u('mmhg', 'Millimeter of mercury', 'mmHg', 133.322387415),
    ],
  },
  energy: {
    key: 'energy',
    label: 'Energy',
    measure: 'energy',
    defaultFrom: 'kcal',
    defaultTo: 'kj',
    sampleValue: 1,
    units: [
      u('j', 'Joule', 'J', 1),
      u('kj', 'Kilojoule', 'kJ', 1000),
      u('cal', 'Calorie', 'cal', 4.184),
      u('kcal', 'Kilocalorie', 'kcal', 4184),
      u('wh', 'Watt hour', 'Wh', 3600),
      u('kwh', 'Kilowatt hour', 'kWh', 3600000),
      u('btu', 'British thermal unit', 'BTU', 1055.05585262),
    ],
  },
  power: {
    key: 'power',
    label: 'Power',
    measure: 'power',
    defaultFrom: 'kw',
    defaultTo: 'hp',
    sampleValue: 1,
    units: [
      u('w', 'Watt', 'W', 1),
      u('kw', 'Kilowatt', 'kW', 1000),
      u('mw', 'Megawatt', 'MW', 1000000),
      u('hp', 'Horsepower (mechanical)', 'hp', 745.6998715822702),
      u('ps', 'Metric horsepower', 'PS', 735.49875),
    ],
  },
  angle: {
    key: 'angle',
    label: 'Angle',
    measure: 'angle',
    defaultFrom: 'deg',
    defaultTo: 'rad',
    sampleValue: 90,
    units: [
      u('deg', 'Degree', '°', 1),
      u('rad', 'Radian', 'rad', 57.29577951308232),
      u('grad', 'Gradian', 'grad', 0.9),
      u('arcmin', 'Arcminute', "'", 0.016666666666666666),
      u('arcsec', 'Arcsecond', '"', 0.0002777777777777778),
      u('turn', 'Turn', 'turn', 360),
    ],
  },
  frequency: {
    key: 'frequency',
    label: 'Frequency',
    measure: 'frequency',
    defaultFrom: 'mhz',
    defaultTo: 'ghz',
    sampleValue: 1,
    units: [
      u('hz', 'Hertz', 'Hz', 1),
      u('khz', 'Kilohertz', 'kHz', 1000),
      u('mhz', 'Megahertz', 'MHz', 1000000),
      u('ghz', 'Gigahertz', 'GHz', 1000000000),
      u('rpm', 'Revolution per minute', 'rpm', 0.016666666666666666),
    ],
  },
  torque: {
    key: 'torque',
    label: 'Torque',
    measure: 'torque',
    defaultFrom: 'nm',
    defaultTo: 'lbft',
    sampleValue: 1,
    units: [
      u('nm', 'Newton meter', 'N·m', 1),
      u('lbft', 'Pound-foot', 'lb·ft', 1.3558179483314004),
      u('lbin', 'Pound-inch', 'lb·in', 0.1129848290276167),
      u('kgfm', 'Kilogram-force meter', 'kgf·m', 9.80665),
    ],
  },
  voltage: {
    key: 'voltage',
    label: 'Voltage',
    measure: 'electric potential',
    defaultFrom: 'v',
    defaultTo: 'mv',
    sampleValue: 1,
    units: [
      u('uv', 'Microvolt', 'µV', 0.000001),
      u('mv', 'Millivolt', 'mV', 0.001),
      u('v', 'Volt', 'V', 1),
      u('kv', 'Kilovolt', 'kV', 1000),
    ],
  },
  current: {
    key: 'current',
    label: 'Current',
    measure: 'electric current',
    defaultFrom: 'a',
    defaultTo: 'ma',
    sampleValue: 1,
    units: [
      u('ua', 'Microampere', 'µA', 0.000001),
      u('ma', 'Milliampere', 'mA', 0.001),
      u('a', 'Ampere', 'A', 1),
      u('ka', 'Kiloampere', 'kA', 1000),
    ],
  },
  charge: {
    key: 'charge',
    label: 'Charge',
    measure: 'electric charge',
    defaultFrom: 'c',
    defaultTo: 'mah',
    sampleValue: 1,
    units: [
      u('uc', 'Microcoulomb', 'µC', 0.000001),
      u('mc', 'Millicoulomb', 'mC', 0.001),
      u('c', 'Coulomb', 'C', 1),
      u('mah', 'Milliamp hour', 'mAh', 3.6),
      u('ah', 'Amp hour', 'Ah', 3600),
    ],
  },
  illuminance: {
    key: 'illuminance',
    label: 'Illuminance',
    measure: 'illuminance',
    defaultFrom: 'lux',
    defaultTo: 'fc',
    sampleValue: 100,
    units: [
      u('lux', 'Lux', 'lx', 1),
      u('fc', 'Foot-candle', 'fc', 10.763910416709722),
      u('phot', 'Phot', 'ph', 10000),
    ],
  },
  'apparent-power': {
    key: 'apparent-power',
    label: 'Apparent power',
    measure: 'apparent power',
    defaultFrom: 'kva',
    defaultTo: 'va',
    sampleValue: 1,
    units: [
      u('va', 'Volt-ampere', 'VA', 1),
      u('kva', 'Kilovolt-ampere', 'kVA', 1000),
      u('mva', 'Megavolt-ampere', 'MVA', 1000000),
    ],
  },
  'reactive-power': {
    key: 'reactive-power',
    label: 'Reactive power',
    measure: 'reactive power',
    defaultFrom: 'kvar',
    defaultTo: 'var',
    sampleValue: 1,
    units: [
      u('var', 'Volt-ampere reactive', 'VAR', 1),
      u('kvar', 'Kilovolt-ampere reactive', 'kVAR', 1000),
      u('mvar', 'Megavolt-ampere reactive', 'MVAR', 1000000),
    ],
  },
  'reactive-energy': {
    key: 'reactive-energy',
    label: 'Reactive energy',
    measure: 'reactive energy',
    defaultFrom: 'kvarh',
    defaultTo: 'varh',
    sampleValue: 1,
    units: [
      u('varh', 'Volt-ampere reactive hour', 'VARh', 1),
      u('kvarh', 'Kilovolt-ampere reactive hour', 'kVARh', 1000),
      u('mvarh', 'Megavolt-ampere reactive hour', 'MVARh', 1000000),
    ],
  },
  'volumetric-flow-rate': {
    key: 'volumetric-flow-rate',
    label: 'Volumetric flow rate',
    measure: 'volumetric flow rate',
    defaultFrom: 'lmin',
    defaultTo: 'gpm',
    sampleValue: 1,
    units: [
      u('m3s', 'Cubic meter per second', 'm³/s', 1),
      u('m3h', 'Cubic meter per hour', 'm³/h', 0.0002777777777777778),
      u('ls', 'Liter per second', 'L/s', 0.001),
      u('lmin', 'Liter per minute', 'L/min', 0.0000166666666666667),
      u('gpm', 'Gallon per minute (US)', 'gal/min', 0.0000630901964),
      u('ft3s', 'Cubic foot per second', 'ft³/s', 0.028316846592),
    ],
  },
};

export function getUnitCategory(key: UnitCategoryKey): UnitCategory {
  return unitCategories[key];
}

export function findUnit(category: UnitCategory, id: string): UnitDefinition | undefined {
  return category.units.find((unit) => unit.id === id);
}

export type UnitConversionResult = {
  value: number;
  fromId: string;
  toId: string;
  fromLabel: string;
  toLabel: string;
  fromSymbol: string;
  toSymbol: string;
  result: number;
  formatted: string;
  expression: string;
  table: Array<{ id: string; name: string; symbol: string; value: number; formatted: string }>;
};

// Format a number for display: trims noisy floating-point tails while keeping
// small values readable (e.g. very tiny conversions keep significant digits).
export function formatUnitValue(value: number): string {
  if (!Number.isFinite(value)) {
    return '';
  }
  if (value === 0) {
    return '0';
  }
  const abs = Math.abs(value);
  let text: string;
  if (abs >= 1e15 || abs < 1e-6) {
    text = value.toExponential(6);
  } else {
    // toPrecision switches to exponential notation once the exponent reaches the
    // precision, which turned 1 TB in bytes into 1.00000000000e+12. Re-parsing the
    // rounded value gives plain digits back, which is what a byte count should read as.
    text = Number(value.toPrecision(abs >= 1 ? 12 : 8)).toString();
  }
  // Drop trailing zeros / stray decimal point from fixed-notation output.
  if (text.includes('e') || text.includes('E')) {
    return text;
  }
  if (text.includes('.')) {
    text = text.replace(/0+$/, '').replace(/\.$/, '');
  }
  return text;
}

export function convertUnit(
  category: UnitCategory,
  rawValue: string,
  fromId: string,
  toId: string,
): UnitConversionResult {
  const value = Number(rawValue);
  if (rawValue.trim() === '' || !Number.isFinite(value)) {
    throw new Error('Enter a valid number to convert.');
  }
  const from = findUnit(category, fromId);
  const to = findUnit(category, toId);
  if (!from || !to) {
    throw new Error('Pick both a source and a target unit.');
  }

  const base = from.toBase(value);
  const result = to.fromBase(base);
  const table = category.units.map((unit) => {
    const converted = unit.fromBase(base);
    return {
      id: unit.id,
      name: unit.name,
      symbol: unit.symbol,
      value: converted,
      formatted: formatUnitValue(converted),
    };
  });

  return {
    value,
    fromId,
    toId,
    fromLabel: from.name,
    toLabel: to.name,
    fromSymbol: from.symbol,
    toSymbol: to.symbol,
    result,
    formatted: formatUnitValue(result),
    expression: `${formatUnitValue(value)} ${from.symbol} = ${formatUnitValue(result)} ${to.symbol}`,
    table,
  };
}

export function createUnitConversionJson(result: UnitConversionResult): string {
  return JSON.stringify(
    {
      input: { value: result.value, unit: result.fromId },
      output: { value: result.result, unit: result.toId },
      expression: result.expression,
      table: result.table.map((row) => ({ unit: row.id, value: row.value })),
    },
    null,
    2,
  );
}
